import React, { useState } from "react";
import { Link } from "react-router-dom";
import AdSenseAd from "../components/AdSenseAd";

const About = () => {
  const [successMsg, setSuccessMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [files, setFiles] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (fileErrors.length > 0) {
      setErrorMsg("Please fix attachment errors before submitting.");
      return;
    }

    const form = e.target;
    const formData = new FormData(form);
    files.forEach((file) => formData.append("attachments", file));

    try {
      const res = await fetch("/send-feedback", {
        method: "POST",
        body: formData,
      });

      if (res.ok) {
        setSuccessMsg("✅ Feedback sent, thank you! We will review it.");
        setErrorMsg(null);
        form.reset();
        form.querySelector('input[type="file"]').value = "";
        setFiles([]);
        setFileErrors([]);
        setTimeout(() => setSuccessMsg(null), 3000);
      } else {
        setErrorMsg("❌ Failed to send feedback.");
      }
    } catch (err) {
      console.error("Submission error:", err);
      setErrorMsg("❌ Failed to send feedback.");
    }
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    const allowedTypes = [
      "application/pdf",
      "text/plain",
      "application/json",
      "image/jpeg",
      "image/png",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const errors = selected
      .filter(
        (file) =>
          file.size > 5 * 1024 * 1024 || !allowedTypes.includes(file.type)
      )
      .map((file) => `❌ ${file.name} (invalid type or > 5MB)`);

    setFiles(selected);
    setFileErrors(errors);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 text-gray-900 dark:text-gray-100">
      <h1 className="text-3xl font-bold mb-6 text-center">About IncogJson</h1>

      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">What is IncogJson?</h2>
        <p className="text-sm leading-relaxed text-justify mb-4">
          <strong>IncogJson</strong> is a browser-based toolkit for validating and converting structured data formats such as JSON, YAML, and XML — all processed locally for maximum privacy. Whether you're formatting an API payload, converting YAML for a Kubernetes manifest, or just want a quick JSON check, IncogJson is here for you.
        </p>
        <ul className="list-disc list-inside text-sm space-y-2">
          <li><strong><Link to="/">JSON Validator:</Link></strong> Validate, pretty-print/lint, compress, decompress, and copy JSON directly in the browser.</li>
          <li><strong><Link to="/json-to-yaml">JSON to YAML:</Link></strong> Convert JSON to YAML (human-readable format) ideal for DevOps and CI/CD workflows.</li>
          <li><strong><Link to="/yaml-to-json">YAML to JSON:</Link></strong> Convert YAML to JSON structure and transform it into a clean for frontend/backend use.</li>
          <li><strong><Link to="/json-to-xml">JSON to XML:</Link></strong> Convert JSON to XML syntax for legacy system compatibility and XML-based services.</li>
          <li><strong>Feedback Form:</strong> Send us your thoughts and suggestions. Optionally attach logs, screenshots, or files (PDF, TXT, JSON, images).</li>
          <li><strong>Privacy by Design:</strong> Everything runs locally in your browser. No uploads. No tracking. No history.</li>
        </ul>
      </section>

      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">Why I Built IncogJson</h2>
        <p className="text-sm leading-relaxed text-justify">
          As an SRE, I frequently work with JSON and YAML while debugging API payloads, infrastructure files, and configuration snippets. I often used popular online tools for formatting or converting—but always with hesitation. What if these tools are saving my input? What if sensitive data like tokens or secrets get exposed?
        </p>
        <p className="text-sm leading-relaxed text-justify mt-4">
          That concern led me to build <strong>IncogJson</strong> — a lightweight, privacy-first utility that works completely inside your browser. No backend storage. No tracking. No data sent to a server. Think of it like a developer's incognito workspace for structured data.
        </p>
      </section>

      {/* AdSense Ad - Placed between content sections */}
      <AdSenseAd adSlot="9698302360" />

      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">Send Feedback</h2>
        <form onSubmit={handleSubmit} className="space-y-4" encType="multipart/form-data">
          <input
            type="text"
            name="name"
            placeholder="Your Name"
            required
            className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white text-black dark:bg-gray-800 dark:text-white"
          />
          <input
            type="email"
            name="email"
            placeholder="Your Email"
            required
            className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white text-black dark:bg-gray-800 dark:text-white"
          />
          <textarea
            name="message"
            rows="4"
            placeholder="Your Message..."
            required
            className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white text-black dark:bg-gray-800 dark:text-white"
          />
          <input
            type="file"
            name="attachments"
            multiple
            accept=".pdf,.txt,.json,.jpg,.jpeg,.png,.docx"
            onChange={handleFileChange}
            className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white text-black dark:bg-gray-800 dark:text-white"
          />

          {files.length > 0 && (
            <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
              {files.map((file, idx) => (
                <li
                  key={idx}
                  className="flex justify-between items-center bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded"
                >
                  <span className="truncate">
                    📎 {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const updated = [...files];
                      updated.splice(idx, 1);
                      setFiles(updated);
                    }}
                    className="ml-4 text-red-500 hover:text-red-700 text-sm transition-colors"
                    aria-label={`Remove ${file.name}`}
                  >
                    ❌ Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          {fileErrors.length > 0 && (
            <div className="bg-red-100 text-red-700 text-sm px-3 py-2 mt-2 rounded">
              {fileErrors.map((err, i) => (
                <div key={i}>{err}</div>
              ))}
            </div>
          )}

          {(successMsg || errorMsg) && (
            <div className={`text-sm px-4 py-2 rounded ${
              successMsg ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
            }`}>
              {successMsg || errorMsg}
            </div>
          )}

          <div className="text-right">
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded shadow transition-colors"
              aria-label="Submit feedback"
            >
              📧 Submit Feedback
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default About;
